import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { Dashboard, DashboardGroup, LoadingState } from '@types'
import { dashboardsApi } from '@api/dashboards'

interface DashboardsState {
  dashboards: Dashboard[]
  selectedDashboard: Dashboard | null
  dashboardGroups: DashboardGroup[]
  loadingState: LoadingState
  error: string | null
  dashboardEdited: boolean
}

const initialState: DashboardsState = {
  dashboards: [],
  selectedDashboard: null,
  dashboardGroups: [],
  loadingState: 'idle',
  error: null,
  dashboardEdited: false,
}

// Async thunks
export const fetchDashboards = createAsyncThunk(
  'dashboards/fetchDashboards',
  async (_, { rejectWithValue }) => {
    try {
      const response = await dashboardsApi.getDashboards()
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch dashboards')
    }
  }
)

export const fetchDashboard = createAsyncThunk(
  'dashboards/fetchDashboard',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await dashboardsApi.getDashboard(id)
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch dashboard')
    }
  }
)

export const createDashboard = createAsyncThunk(
  'dashboards/createDashboard',
  async (dashboardData: Partial<Dashboard>, { rejectWithValue }) => {
    try {
      const response = await dashboardsApi.createDashboard(dashboardData)
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create dashboard')
    }
  }
)

export const updateDashboard = createAsyncThunk(
  'dashboards/updateDashboard',
  async ({ id, data }: { id: number; data: Partial<Dashboard> }, { rejectWithValue }) => {
    try {
      const response = await dashboardsApi.updateDashboard(id, data)
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update dashboard')
    }
  }
)

export const deleteDashboard = createAsyncThunk(
  'dashboards/deleteDashboard',
  async (id: number, { rejectWithValue }) => {
    try {
      await dashboardsApi.deleteDashboard(id)
      return id
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete dashboard')
    }
  }
)

const dashboardsSlice = createSlice({
  name: 'dashboards',
  initialState,
  reducers: {
    setSelectedDashboard: (state, action: PayloadAction<Dashboard | null>) => {
      state.selectedDashboard = action.payload
    },
    setDashboardEdited: (state, action: PayloadAction<boolean>) => {
      state.dashboardEdited = action.payload
    },
    updateDashboardGroup: (state, action: PayloadAction<{ groupId: number; data: Partial<DashboardGroup> }>) => {
      if (state.selectedDashboard && state.selectedDashboard.dashboard_groups) {
        const group = state.selectedDashboard.dashboard_groups.find(g => g.id === action.payload.groupId)
        if (group) {
          Object.assign(group, action.payload.data)
          state.dashboardEdited = true
        }
      }
    },
    addDashboardGroup: (state, action: PayloadAction<DashboardGroup>) => {
      if (state.selectedDashboard) {
        if (!state.selectedDashboard.dashboard_groups) {
          state.selectedDashboard.dashboard_groups = []
        }
        state.selectedDashboard.dashboard_groups.push(action.payload)
        state.dashboardEdited = true
      }
    },
    removeDashboardGroup: (state, action: PayloadAction<number>) => {
      if (state.selectedDashboard && state.selectedDashboard.dashboard_groups) {
        state.selectedDashboard.dashboard_groups = state.selectedDashboard.dashboard_groups.filter(
          group => group.id !== action.payload
        )
        state.dashboardEdited = true
      }
    },
    clearError: (state) => {
      state.error = null
    },
    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Fetch Dashboards
      .addCase(fetchDashboards.pending, (state) => {
        state.loadingState = 'pending'
        state.error = null
      })
      .addCase(fetchDashboards.fulfilled, (state, action) => {
        state.loadingState = 'succeeded'
        const payload = action.payload as any;
        if (Array.isArray(payload)) {
          state.dashboards = payload;
        } else if (payload && Array.isArray(payload.data)) {
          state.dashboards = payload.data;
        } else if (payload && Array.isArray(payload.results)) {
          state.dashboards = payload.results;
        } else {
          state.dashboards = [];
        }
      })
      .addCase(fetchDashboards.rejected, (state, action) => {
        state.loadingState = 'failed'
        state.error = action.payload as string
      })

      // Fetch Dashboard
      .addCase(fetchDashboard.fulfilled, (state, action) => {
        state.selectedDashboard = action.payload.data
        // Also update in dashboards array if it exists
        const index = state.dashboards.findIndex(d => d.id === action.payload.data.id)
        if (index !== -1) {
          state.dashboards[index] = action.payload.data
        }
      })

      // Create Dashboard
      .addCase(createDashboard.fulfilled, (state, action) => {
        state.dashboards.push(action.payload.data)
        state.selectedDashboard = action.payload.data
        state.dashboardEdited = false
      })

      // Update Dashboard
      .addCase(updateDashboard.fulfilled, (state, action) => {
        const index = state.dashboards.findIndex(d => d.id === action.payload.data.id)
        if (index !== -1) {
          state.dashboards[index] = action.payload.data
        }
        if (state.selectedDashboard?.id === action.payload.data.id) {
          state.selectedDashboard = action.payload.data
        }
        state.dashboardEdited = false
      })

      // Delete Dashboard
      .addCase(deleteDashboard.fulfilled, (state, action) => {
        state.dashboards = state.dashboards.filter(d => d.id !== action.payload)
        if (state.selectedDashboard?.id === action.payload) {
          state.selectedDashboard = null
        }
      })
  },
})

export const {
  setSelectedDashboard,
  setDashboardEdited,
  updateDashboardGroup,
  addDashboardGroup,
  removeDashboardGroup,
  clearError,
  resetState,
} = dashboardsSlice.actions

export default dashboardsSlice.reducer
